import React, {useEffect, useRef, useState, createRef} from 'react';
import FFTChartMerge from '@user-components/FFTChartMerge';
import {Grid, Box, IconButton, TextField, Button, MenuItem, Typography, FormControlLabel, Checkbox} from '@material-ui/core';
import {CheckCircle, Cancel} from '@material-ui/icons';
import {makeStyles} from "@material-ui/core/styles";
import ColorHelper from '@helper/ColorHelper';
import SocketProvider from '@user-provider/SocketProvider';
import MeasureHelper from '@helper/MeasureHelper';
import TimeCount from '@user-pages/exercise/common/TimeCount';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SettingsIcon from '@material-ui/icons/Settings';
import Axios from 'axios';
import {showSnackbar} from '@helper/SnackbarHelper';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FrequencyEqualizer from './FrequencyEqualizer';
import FFTLineChart from './FFTLineChart';
import FFTLineChart3D from './FFTLineChart3D';

function DraggableBox({children, className}) {
    const boxRef = useRef(null)
    const dragState = useRef({isDragging: false, startX: 0, startY: 0, origX: 0, origY: 0})
    const [pos, setPos] = useState({x: 0, y: 0})

    const handleMouseDown = (e) => {
        // canvas 위에서는 드래그 시작하지 않음 (EQ 조작용)
        if (e.target.tagName === 'CANVAS') return
        dragState.current = {
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY,
            origX: pos.x,
            origY: pos.y
        }
        e.preventDefault()
    }

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragState.current.isDragging) return
            const dx = e.clientX - dragState.current.startX
            const dy = e.clientY - dragState.current.startY
            setPos({x: dragState.current.origX + dx, y: dragState.current.origY + dy})
        }
        const handleMouseUp = () => {
            dragState.current.isDragging = false
        }
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [])

    return (
        <Box
            ref={boxRef}
            className={className}
            style={{transform: `translate(${pos.x}px, ${pos.y}px)`, cursor: 'move'}}
            onMouseDown={handleMouseDown}
        >
            {children}
        </Box>
    )
}

function StepInput({label, value, onChange, step = 0.1, width = 100, min, max, disabled, classes}) {
    const intervalRef = useRef(null)
    const timeoutRef = useRef(null)
    const valueRef = useRef(value)
    valueRef.current = value

    const roundValue = (v) => Math.round(v * 100) / 100

    const applyDelta = (delta) => {
        const next = roundValue(parseFloat(valueRef.current) + delta)
        let clamped = min !== undefined ? Math.max(min, next) : next
        clamped = max !== undefined ? Math.min(max, clamped) : clamped
        onChange(clamped)
    }

    const startRepeat = (delta) => {
        stopRepeat()
        applyDelta(delta)
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                applyDelta(delta)
            }, 80)
        }, 300)
    }

    const stopRepeat = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    useEffect(() => {
        return () => stopRepeat()
    }, [])

    return (
        <Box display="flex" alignItems="center">
            <TextField
                type="number"
                label={label}
                variant="outlined"
                size="small"
                value={value}
                onChange={(e) => {
                    let val = parseFloat(e.target.value) || 0
                    if (min !== undefined) val = Math.max(min, val)
                    if (max !== undefined) val = Math.min(max, val)
                    onChange(val)
                }}
                disabled={disabled}
                InputProps={{className: classes.input}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                inputProps={{step, min, max}}
                style={{width}}
            />
            <Box display="flex" flexDirection="column" style={{marginLeft: 2}}>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    disabled={disabled}
                    onMouseDown={() => !disabled && startRepeat(step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropUpIcon style={{fontSize: 20}}/>
                </IconButton>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    disabled={disabled}
                    onMouseDown={() => !disabled && startRepeat(-step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropDownIcon style={{fontSize: 20}}/>
                </IconButton>
            </Box>
        </Box>
    )
}

const {measureTypeToCode} = MeasureHelper
const MeasureHzType = 'measureBand'

const SOURCE_DEVICE = 'device'
const SOURCE_FILE = 'file'
const SOURCE_PRESCRIPTION = 'prescription'

const STORAGE_KEY = 'measureBandLocalSettings'

const loadSettings = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        return saved ? JSON.parse(saved) : {}
    } catch { return {} }
}

const saveSettings = (settings) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {}
}

const MeasureBandLocal = () => {
    const classes = useStyles()
    const measureRef = useRef()
    const timeCountRef = useRef()

    const saved = useRef(loadSettings()).current

    const [source, setSource] = useState(saved.source || SOURCE_DEVICE)
    const [measureTime, setMeasureTime] = useState(saved.measureTime ?? 120)
    const [highValue, setHighValue] = useState(saved.highValue ?? 0)
    const [lowValue, setLowValue] = useState(saved.lowValue ?? 55)
    const [isRunning, setIsRunning] = useState(false)
    const [speed, setSpeed] = useState(saved.speed ?? 1)
    const [amplification, setAmplification] = useState(saved.amplification ?? 1.5)
    const [noiseFilter, setNoiseFilter] = useState(saved.noiseFilter ?? 'A')
    const [loop, setLoop] = useState(saved.loop ?? false)
    const loopRef = useRef(loop)
    const [chartMode, setChartMode] = useState(saved.chartMode || '3d')
    const lineChartRef = useRef()
    const lineChart3DRef = useRef()
    const [initNotice, setInitNotice] = useState(false)
    const [fileReady, setFileReady] = useState(false)
    const [fileName, setFileName] = useState('')
    const [deviceConnected, setDeviceConnected] = useState(false)
    const [resolution, setResolution] = useState(saved.resolution || 'normal')
    const [prescriptions, setPrescriptions] = useState([])
    const [selectedPrescriptionID, setSelectedPrescriptionID] = useState('')
    const [prescriptionDataReady, setPrescriptionDataReady] = useState(false)
    const [noiseProfileEnabled] = useState(false)
    const [noiseProfileName] = useState('')
    const [eqEnabled, setEqEnabled] = useState(saved.eqEnabled ?? false)
    const [ampEqEnabled, setAmpEqEnabled] = useState(saved.ampEqEnabled ?? false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const resolutionRef = useRef(resolution)
    const eqEnabledRef = useRef(eqEnabled)
    const eqCurveRef = useRef(saved.eqCurve && saved.eqCurve.length === 30 ? saved.eqCurve : new Array(30).fill(0))
    const eqRef = useRef()
    const ampEqEnabledRef = useRef(ampEqEnabled)
    const ampEqCurveRef = useRef(saved.ampEqCurve && saved.ampEqCurve.length === 30 ? saved.ampEqCurve : new Array(30).fill(1.0))
    const ampEqRef = useRef()
    useEffect(() => { resolutionRef.current = resolution }, [resolution])
    useEffect(() => { loopRef.current = loop }, [loop])
    useEffect(() => { eqEnabledRef.current = eqEnabled }, [eqEnabled])
    useEffect(() => { ampEqEnabledRef.current = ampEqEnabled }, [ampEqEnabled])
    const speedOptions = [0.2, 0.5, 0.7, 1, 1.2, 1.5, 1.7, 2]
    const noiseFilterOptions = [
        {value: 'A', label: 'A', desc: 'Spectral Smoothing — 3-bin 이동평균, 랜덤 노이즈 피크 완화, 신호 왜곡 최소'},
        {value: 'D', label: 'D', desc: 'Wiener Filter — SNR 추정 후 주파수별 감쇠, 가장 정밀'},
        {value: '', label: 'Off', desc: '노이즈 제거 없음'},
    ]

    // 설정 변경 시 자동 저장
    useEffect(() => {
        saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqEnabled, eqCurve: eqCurveRef.current, ampEqEnabled, ampEqCurve: ampEqCurveRef.current, loop, chartMode})
    }, [source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqEnabled, ampEqEnabled, loop, chartMode])

    const isFileMode = source === SOURCE_FILE
    const isPrescriptionMode = source === SOURCE_PRESCRIPTION
    const canPlay = isFileMode ? fileReady : isPrescriptionMode ? prescriptionDataReady : deviceConnected

    useEffect(() => {
        if (!window.app) {
            window.app = {}
        }

        SocketProvider.connect(() => {}, window, MeasureHzType)

        SocketProvider.onMeasure = (data) => {
            if (measureRef.current) {
                measureRef.current.update(data)
            }
        }

        // 기기 연결 상태 체크: C# 클라이언트에게 물어봄
        const checkDeviceStatus = () => {
            try {
                if (window.BRHW?.isDeviceConnected) {
                    const result = window.BRHW.isDeviceConnected()
                    // CefSharp은 Promise를 반환할 수 있음
                    if (result && typeof result.then === 'function') {
                        result.then((connected) => setDeviceConnected(!!connected))
                    } else {
                        setDeviceConnected(!!result)
                    }
                }
            } catch (e) {
                setDeviceConnected(false)
            }
        }
        checkDeviceStatus()
        const deviceCheckInterval = setInterval(checkDeviceStatus, 3000)

        // C#에서 연결 상태가 변경되면 콜백으로도 받을 수 있음
        window.app.onDeviceStatusChanged = (connected) => {
            setDeviceConnected(!!connected)
        }

        const handleFFTData = (data) => {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data
                // 증폭 EQ 적용
                if (ampEqEnabledRef.current && ampEqCurveRef.current && parsed.ch1FFT && parsed.ch2FFT) {
                    const amp = ampEqCurveRef.current
                    parsed.ch1FFT = parsed.ch1FFT.map((v, i) => i < amp.length ? v * amp[i] : v)
                    parsed.ch2FFT = parsed.ch2FFT.map((v, i) => i < amp.length ? v * amp[i] : v)
                }
                // 노이즈 제거 EQ 적용
                if (eqEnabledRef.current && eqCurveRef.current && parsed.ch1FFT && parsed.ch2FFT) {
                    const curve = eqCurveRef.current
                    parsed.ch1FFT = parsed.ch1FFT.map((v, i) => i < curve.length ? Math.max(0, v - curve[i]) : v)
                    parsed.ch2FFT = parsed.ch2FFT.map((v, i) => i < curve.length ? Math.max(0, v - curve[i]) : v)
                }
                if (measureRef.current) {
                    measureRef.current.update(parsed)
                }
                if (lineChartRef.current) {
                    lineChartRef.current.update(parsed)
                }
                if (lineChart3DRef.current) {
                    lineChart3DRef.current.addData(parsed.time, parsed.ch1FFT, parsed.ch2FFT)
                }
                // FFT 데이터의 time으로 타이머 업데이트
                if (parsed.time && timeCountRef.current) {
                    timeCountRef.current.update(parsed.time)
                }
                // 고해상도 모드: 10초 지나면 안내 팝업 해제
                if (parsed.time >= 10) {
                    setInitNotice(false)
                }
            } catch (e) {
                console.error('[MeasureBandLocal] FFT 데이터 파싱 오류:', e)
            }
        }

        // 1Hz 40bin FFT (일반 모드에서만 처리)
        window.app.onLocalFFTData = (data) => {
            if (resolutionRef.current === 'normal') handleFFTData(data)
        }
        // 0.1Hz 496bin FFT (고해상도 모드에서만 처리)
        window.app.onLocalEEGFFTData = (data) => {
            if (resolutionRef.current === 'high') handleFFTData(data)
        }

        window.app.onMeasureFileOpened = (name) => {
            console.log('[MeasureBandLocal] 파일 열림:', name)
            setFileReady(true)
            setFileName(name || '')
        }

        window.app.onMeasureFilePlayDone = () => {
            console.log('[MeasureBandLocal] 재생 완료, loop:', loopRef.current)
            if (loopRef.current) {
                // 루프: 차트 초기화 후 자동 재생
                if (measureRef.current) measureRef.current.clear()
                if (lineChart3DRef.current) lineChart3DRef.current.clearCanvas()
                if (lineChartRef.current) lineChartRef.current.clear()
                if (timeCountRef.current) {
                    timeCountRef.current.resetInterval()
                    timeCountRef.current.update(0)
                }
                try {
                    if (window.BRHW?.startMeasureFilePlay) {
                        window.BRHW.startMeasureFilePlay()
                    }
                } catch (e) {}
            } else {
                setIsRunning(false)
            }
        }

        window.app.onNoiseProfileLoaded = (name) => {
            console.log('[MeasureBandLocal] 노이즈 프로파일 로드:', name)
        }

        return () => {
            if (measureRef.current) {
                measureRef.current.clear()
            }
            SocketProvider.measureStop(true).then()
            clearInterval(deviceCheckInterval)
            if (window.app) {
                window.app.onLocalFFTData = null
                window.app.onLocalEEGFFTData = null
                window.app.onMeasureFileOpened = null
                window.app.onMeasureFilePlayDone = null
                window.app.onNoiseProfileLoaded = null
                window.app.onDeviceStatusChanged = null
            }
        }
    }, [])

    const handleStart = async () => {
        if (isRunning) return
        if (measureRef.current) measureRef.current.clear()
        if (lineChart3DRef.current) lineChart3DRef.current.clearCanvas()
        if (lineChartRef.current) lineChartRef.current.clear()
        timeCountRef.current.resetInterval()
        timeCountRef.current.update(0)

        // 양쪽 모드 모두 설정값 전달
        try { if (window.BRHW?.setPlaySpeed) window.BRHW.setPlaySpeed(speed) } catch (e) {}
        try { if (window.BRHW?.setResolution) window.BRHW.setResolution(resolution) } catch (e) {}
        try { if (window.BRHW?.setAmplification) window.BRHW.setAmplification(parseFloat(amplification) || 1) } catch (e) {}
        try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(parseFloat(highValue) || 0) } catch (e) {}
        try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(parseFloat(lowValue) || 0) } catch (e) {}
        try { if (window.BRHW?.setNoiseFilter) window.BRHW.setNoiseFilter(noiseFilter) } catch (e) {}

        // 초반 10초 안내 팝업 (고해상도일 때만, FFT data.time >= 10 에서 해제)
        if (resolution === 'high') {
            setInitNotice(true)
        }

        if (isFileMode) {
            // 파일 모드: C#에 재생 시작 요청
            try {
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 파일 재생 오류:', e)
            }
            setIsRunning(true)
        } else if (isPrescriptionMode) {
            // 회차 모드: raw 데이터를 서버에서 가져와서 C# 클라이언트에 전달
            try {
                const res = await Axios.get(`/api/manage/prescriptions-test/${selectedPrescriptionID}/raw`)
                if (res.data && res.data.data && window.BRHW?.loadRawData) {
                    window.BRHW.loadRawData(res.data.data)
                }
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 재생 오류:', e)
            }
            setIsRunning(true)
        } else {
            // 기기 모드: C# 브릿지로 기기 측정 시작
            try {
                if (window.BRHW?.deviceMeasureStart) {
                    window.BRHW.deviceMeasureStart(measureTime, 'rest')
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 시작 오류:', e)
            }
            setIsRunning(true)
        }
    }

    const handleStop = async () => {
        if (!isRunning) return

        if (isFileMode || isPrescriptionMode) {
            try {
                if (window.BRHW?.stopMeasureFilePlay) {
                    window.BRHW.stopMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 재생 중지 오류:', e)
            }
        } else {
            try {
                if (window.BRHW?.DeviceMeasureStop) {
                    window.BRHW.DeviceMeasureStop()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 중지 오류:', e)
            }
        }

        timeCountRef.current.pauseInterval()
        setIsRunning(false)
    }

    const handleSourceChange = async (newSource) => {
        if (isRunning) return
        setSource(newSource)
        setFileReady(false)
        setFileName('')
        setSelectedPrescriptionID('')
        setPrescriptionDataReady(false)
        try {
            if (window.BRHW?.setInputSource) {
                window.BRHW.setInputSource(newSource)
            }
        } catch (e) {
            console.error('[MeasureBandLocal] 입력소스 전송 오류:', e)
        }

        if (newSource === SOURCE_PRESCRIPTION) {
            try {
                const res = await Axios.get('/api/manage/prescriptions-test/ids')
                setPrescriptions(res.data.prescriptions || [])
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 목록 조회 오류:', e)
                setPrescriptions([])
            }
        }
    }

    return (
        <Box style={{position: 'relative'}}>
            {/* 상단 컨트롤 바 */}
            <Box className={classes.statusBar}>
                <Grid container alignItems="center" spacing={2} style={{height: '100%', padding: '0 16px'}}>
                    {/* 소스 선택 */}
                    <Grid item>
                        <TextField
                            select
                            label="입력소스"
                            variant="outlined"
                            size="small"
                            value={source}
                            onChange={(e) => handleSourceChange(e.target.value)}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 150}}
                        >
                            <MenuItem value={SOURCE_DEVICE}>기기에서 측정</MenuItem>
                            <MenuItem value={SOURCE_FILE}>파일에서 가져오기</MenuItem>
                            <MenuItem value={SOURCE_PRESCRIPTION}>회차 선택</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 회차 선택 모드: 회차 목록 (검색 가능) */}
                    {isPrescriptionMode && prescriptions.length > 0 && (
                        <Grid item>
                            <Autocomplete
                                options={prescriptions}
                                getOptionLabel={(option) => String(option.id)}
                                value={prescriptions.find(p => p.id === selectedPrescriptionID) || null}
                                onChange={async (e, newValue) => {
                                    const id = newValue ? newValue.id : ''
                                    setSelectedPrescriptionID(id)
                                    setPrescriptionDataReady(false)
                                    if (id) {
                                        try {
                                            const res = await Axios.get(`/api/manage/prescriptions-test/${id}/raw`)
                                            if (res.data && res.data.data) {
                                                if (window.BRHW?.loadRawData) {
                                                    window.BRHW.loadRawData(res.data.data)
                                                }
                                                setPrescriptionDataReady(true)
                                            } else {
                                                showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                            }
                                        } catch (ex) {
                                            showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                        }
                                    }
                                }}
                                disabled={isRunning}
                                size="small"
                                style={{width: 160}}
                                classes={{inputRoot: classes.autocompleteInput, popupIndicator: classes.selectIcon, clearIndicator: classes.selectIcon}}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="회차"
                                        variant="outlined"
                                        InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* 측정방법 */}
                    <Grid item>
                        <TextField
                            select
                            label="측정방법"
                            variant="outlined"
                            size="small"
                            value={resolution}
                            onChange={(e) => {
                                setResolution(e.target.value)
                                try { if (window.BRHW?.setResolution) window.BRHW.setResolution(e.target.value) } catch (ex) {}
                            }}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 130}}
                        >
                            <MenuItem value="normal">일반 (1Hz)</MenuItem>
                            <MenuItem value="high">고해상도 (0.1Hz)</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 기기 모드: 측정시간 */}
                    {source === SOURCE_DEVICE && (
                        <Grid item>
                            <TextField
                                type="number"
                                label="측정시간(초)"
                                variant="outlined"
                                size="small"
                                value={measureTime}
                                onChange={(e) => setMeasureTime(parseInt(e.target.value) || 0)}
                                disabled={isRunning}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel}}
                                style={{width: 130}}
                            />
                        </Grid>
                    )}

                    {/* 파일 모드: 측정데이터 열기 */}
                    {isFileMode && (
                        <Grid item>
                            <Button
                                variant="contained"
                                className={classes.openFileBtn}
                                disabled={isRunning}
                                onClick={() => {
                                    try {
                                        if (window.BRHW?.openMeasureFile) {
                                            window.BRHW.openMeasureFile(speed, parseFloat(highValue) || 0, parseFloat(lowValue) || 0, parseFloat(amplification) || 0, noiseFilter)
                                        }
                                    } catch (e) {
                                        console.error('[MeasureBandLocal] 파일 열기 오류:', e)
                                    }
                                }}
                            >
                                {fileReady ? `📂 ${fileName}` : '측정데이터 열기'}
                            </Button>
                        </Grid>
                    )}

                    {/* 시작/중지 */}
                    <Grid item>
                        {!isRunning ? (
                            <IconButton
                                className={classes.startBtn}
                                onClick={handleStart}
                                disabled={!canPlay}
                            >
                                <PlayArrowIcon/>
                            </IconButton>
                        ) : (
                            <IconButton className={classes.stopBtn} onClick={handleStop}>
                                <StopIcon/>
                            </IconButton>
                        )}
                    </Grid>

                    {/* 타이머 */}
                    <Grid item style={{color: '#fff'}}>
                        <TimeCount ref={timeCountRef}/>
                    </Grid>

                    {/* 구분선 */}
                    <Grid item style={{paddingLeft: 24, paddingRight: 24}}>
                        <Box className={classes.divider}/>
                    </Grid>

                    {/* High / Low / 증폭 입력 */}
                    <Grid item>
                        <StepInput
                            label="하이패스"
                            value={highValue}
                            step={resolution === 'high' ? 0.1 : 1}
                            min={0}
                            disabled={initNotice}
                            classes={classes}
                            onChange={(v) => {
                                setHighValue(v)
                                try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(parseFloat(v) || 0) } catch (ex) {}
                            }}
                        />
                    </Grid>
                    {/* 설정 버튼 */}
                    <Grid item>
                        <IconButton
                            className={classes.settingsBtn}
                            onClick={() => setSettingsOpen(!settingsOpen)}
                        >
                            <SettingsIcon/>
                        </IconButton>
                    </Grid>

                    {/* 새로고침 */}
                    <Grid item style={{marginLeft: 'auto'}}>
                        <Button
                            variant="contained"
                            className={classes.refreshBtn}
                            onClick={() => window.location.reload()}
                            startIcon={<RefreshIcon/>}
                        >
                            새로고침
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* 설정 팝업 */}
            {settingsOpen && (
                <DraggableBox className={classes.settingsPopup}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{marginBottom: 12}}>
                        <Typography style={{color: '#fff', fontSize: 14, fontWeight: 'bold'}}>설정</Typography>
                        <IconButton size="small" onClick={() => setSettingsOpen(false)} style={{color: '#fff'}}>
                            <Cancel style={{fontSize: 18}}/>
                        </IconButton>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        {/* 로우패스 */}
                        <Grid item>
                            <StepInput
                                label="로우패스"
                                value={lowValue}
                                step={resolution === 'high' ? 0.1 : 1}
                                min={0}
                                max={55}
                                disabled={initNotice}
                                classes={classes}
                                onChange={(v) => {
                                    setLowValue(v)
                                    try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(parseFloat(v) || 0) } catch (ex) {}
                                }}
                            />
                        </Grid>

                        {/* 노이즈필터 */}
                        <Grid item>
                            <TextField
                                select
                                label="노이즈필터"
                                variant="outlined"
                                size="small"
                                disabled={initNotice}
                                value={noiseFilter}
                                onChange={(e) => {
                                    setNoiseFilter(e.target.value)
                                    try { if (window.BRHW?.setNoiseFilter) window.BRHW.setNoiseFilter(e.target.value) } catch (ex) {}
                                }}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                SelectProps={{classes: {icon: classes.selectIcon}}}
                                style={{width: 100}}
                            >
                                {noiseFilterOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* 배속 */}
                        <Grid item>
                            <TextField
                                select
                                label="배속"
                                variant="outlined"
                                size="small"
                                value={speed}
                                onChange={(e) => {
                                    const s = parseFloat(e.target.value)
                                    setSpeed(s)
                                    try {
                                        if (window.BRHW?.setPlaySpeed) {
                                            window.BRHW.setPlaySpeed(s)
                                        }
                                    } catch (ex) {
                                        console.error('[MeasureBandLocal] 속도 전송 오류:', ex)
                                    }
                                }}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                SelectProps={{classes: {icon: classes.selectIcon}}}
                                style={{width: 90}}
                            >
                                {speedOptions.map((s) => (
                                    <MenuItem key={s} value={s}>{s}x</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* 루프 */}
                        <Grid item>
                            <Button
                                variant={loop ? 'contained' : 'outlined'}
                                className={loop ? classes.loopBtnActive : classes.loopBtn}
                                size="small"
                                onClick={() => setLoop(!loop)}
                            >
                                루프 {loop ? 'ON' : 'OFF'}
                            </Button>
                        </Grid>

                        {/* 차트 모드 */}
                        <Grid item>
                            <TextField
                                select
                                label="차트"
                                variant="outlined"
                                size="small"
                                value={chartMode}
                                onChange={(e) => setChartMode(e.target.value)}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                SelectProps={{classes: {icon: classes.selectIcon}}}
                                style={{width: 120}}
                            >
                                <MenuItem value="3d">3D Bar</MenuItem>
                                <MenuItem value="3dline">3D Line</MenuItem>
                                <MenuItem value="2d">2D Line</MenuItem>
                                <MenuItem value="all">모두 표시</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>

                    {/* 노이즈 제거 EQ */}
                    <Box style={{marginTop: 16}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={eqEnabled}
                                    onChange={(e) => setEqEnabled(e.target.checked)}
                                    style={{color: '#fff', padding: '0 4px'}}
                                    size="small"
                                />
                            }
                            label={<span style={{color: '#fff', fontSize: 13}}>노이즈 제거 EQ</span>}
                            style={{marginBottom: 4}}
                        />
                        {eqEnabled && (
                            <FrequencyEqualizer
                                ref={eqRef}
                                width={700}
                                height={250}
                                savedCurve={saved.eqCurve}
                                label="노이즈 제거 EQ (0~30Hz)"
                                onChange={(curve) => {
                                    eqCurveRef.current = curve
                                    saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqEnabled, eqCurve: curve, ampEqEnabled, ampEqCurve: ampEqCurveRef.current})
                                    try {
                                        if (window.BRHW?.setFrequencyFilter) {
                                            window.BRHW.setFrequencyFilter(JSON.stringify(curve))
                                        }
                                    } catch (ex) {}
                                }}
                            />
                        )}
                    </Box>

                    {/* 증폭 EQ */}
                    <Box style={{marginTop: 12}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ampEqEnabled}
                                    onChange={(e) => setAmpEqEnabled(e.target.checked)}
                                    style={{color: '#fff', padding: '0 4px'}}
                                    size="small"
                                />
                            }
                            label={<span style={{color: '#fff', fontSize: 13}}>증폭 EQ</span>}
                            style={{marginBottom: 4}}
                        />
                        {ampEqEnabled && (
                            <FrequencyEqualizer
                                ref={ampEqRef}
                                width={700}
                                height={250}
                                savedCurve={saved.ampEqCurve}
                                defaultValue={1.0}
                                min={0}
                                max={10}
                                label="증폭 EQ (0~30Hz)"
                                onChange={(curve) => {
                                    ampEqCurveRef.current = curve
                                    saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqEnabled, eqCurve: eqCurveRef.current, ampEqEnabled, ampEqCurve: curve})
                                }}
                            />
                        )}
                    </Box>
                </DraggableBox>
            )}

            {/* 연결 상태 - 툴바 아래 왼쪽 */}
            {source === SOURCE_DEVICE && (
                <Box className={classes.deviceStatus}>
                    {deviceConnected ? (
                        <CheckCircle style={{color: '#4CAF50', fontSize: 16, marginRight: 4}}/>
                    ) : (
                        <Cancel style={{color: '#f44336', fontSize: 16, marginRight: 4}}/>
                    )}
                    <Typography style={{color: '#000', fontSize: 12}}>
                        {deviceConnected ? '연결됨' : '신호없음'}
                    </Typography>
                </Box>
            )}

            {/* 노이즈필터 설명 */}
            {noiseFilterOptions.find(o => o.value === noiseFilter) && (
                <Box className={classes.noiseDesc}>
                    {noiseFilterOptions.find(o => o.value === noiseFilter).desc}
                </Box>
            )}

            {/* 초반 10초 안내 */}
            {initNotice && (
                <Box className={classes.initNotice}>
                    0.1Hz 단위로 분석하기 위해 10초 동안의 데이터를 수집합니다. 11초부터 측정 데이터입니다.
                </Box>
            )}

            {/* 3D Bar 차트 */}
            {(chartMode === '3d' || chartMode === 'all') && (
                <Box className={'innerViewport'} style={{marginTop: 0}}>
                    <LocalRealtimeMeasure ref={measureRef}/>
                </Box>
            )}

            {/* 3D Line 차트 */}
            {(chartMode === '3dline' || chartMode === 'all') && (
                <Box className={'innerViewport'} style={{marginTop: 0}}>
                    <FFTLineChart3D ref={lineChart3DRef} height={700}/>
                </Box>
            )}

            {/* 2D Line 차트 */}
            {(chartMode === '2d' || chartMode === 'all') && (
                <Box style={{padding: '0 16px 16px'}}>
                    <FFTLineChart ref={lineChartRef} width={window.innerWidth - 40} height={350} maxHz={50}/>
                </Box>
            )}
        </Box>
    )
}

class LocalRealtimeMeasure extends React.Component {
    fftChartOptions = {
        graphWireFrame: false,
        graphWireFrameColor: 0x666666,
        hzSpacing: 10,
        hzCount: 60,
        cone: false,
        gridSize: 0.7,
        mvSize: 0.3,
        timeHzGrid: true,
        timeMvGrid: true,
        mvHzGrid: true,
        mvGuideLineXY: false,
        timeGuide: true,
        graphColors: ColorHelper.getColors(),
        height: 900,
        brainType: 'both',
        cameraType: 'side',
        noiseCode: 'A'
    }

    constructor(props) {
        super(props)
        this.fftChartRef = createRef(null)
    }

    update(data) {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.addData(data.time, data.ch1FFT, data.ch2FFT)
        }
    }

    clear() {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.clearCanvas()
        }
    }

    componentDidMount() {
        this.fftChartRef.current.initRender()
    }

    render() {
        return (
            <Box>
                <Box padding={2}>
                    <Grid container style={{width: '100%', position: 'relative'}}>
                        <Grid item xs={12} style={{position: 'relative'}}>
                            <FFTChartMerge label={'Brain'} options={this.fftChartOptions} ref={this.fftChartRef}/>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        )
    }
}

const statusBarHeight = 60

const useStyles = makeStyles(() => ({
    statusBar: {
        position: 'absolute',
        top: 0,
        paddingTop: 20,
        paddingBottom: 10,
        left: 0,
        width: '100%',
        height: statusBarHeight + 20,
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 99,
    },
    input: {
        color: '#fff',
        height: 36,
        '& input': {
            textAlign: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.7)',
        },
    },
    inputLabel: {
        color: 'rgba(255,255,255,0.7)',
        '&.Mui-focused': {
            color: '#FFC004',
        },
    },
    startBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(255,0,5,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    stopBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
    },
    divider: {
        width: 1,
        height: 36,
        backgroundColor: 'rgba(255,255,255,0.3)',
    },
    openFileBtn: {
        backgroundColor: '#1976d2',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#1565c0',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(25,118,210,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    resetBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    refreshBtn: {
        backgroundColor: '#757575',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#616161',
        },
    },
    stepBtn: {
        color: '#fff',
        padding: 0,
        width: 20,
        height: 18,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.15)',
        },
    },
    selectIcon: {
        color: 'rgba(255,255,255,0.7)',
    },
    autocompleteInput: {
        color: '#fff',
        height: 36,
        padding: '0 8px !important',
        display: 'flex',
        alignItems: 'center',
        marginTop: -4,
        '& .MuiAutocomplete-input': {
            padding: '0 4px !important',
            color: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
    },
    deviceStatus: {
        position: 'absolute',
        top: statusBarHeight + 30,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        zIndex: 100,
    },
    settingsBtn: {
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.15)',
        },
    },
    settingsPopup: {
        position: 'absolute',
        top: statusBarHeight + 35,
        left: 8,
        zIndex: 99,
        padding: 16,
        backgroundColor: 'rgba(0,0,0,0.9)',
        borderRadius: 8,
        maxWidth: 750,
        border: '1px solid rgba(255,255,255,0.2)',
    },
    eqContainer: {
        position: 'absolute',
        top: statusBarHeight + 35,
        left: 8,
        zIndex: 98,
        padding: 8,
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: 6,
    },
    ampEqContainer: {
        position: 'absolute',
        top: statusBarHeight + 35,
        right: 8,
        zIndex: 98,
        padding: 8,
        backgroundColor: 'rgba(0,50,0,0.7)',
        borderRadius: 6,
    },
    noiseDesc: {
        position: 'absolute',
        top: statusBarHeight + 30,
        right: 8,
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'rgba(255,255,255,0.9)',
        fontSize: 16,
        padding: '6px 12px',
        borderRadius: 4,
        zIndex: 100,
    },
    initNotice: {
        position: 'absolute',
        top: statusBarHeight + 30,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: '#FFC004',
        fontSize: 14,
        fontWeight: 'bold',
        padding: '10px 24px',
        borderRadius: 8,
        zIndex: 100,
        whiteSpace: 'nowrap',
    },
    speedBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        minWidth: 50,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    speedBtnActive: {
        backgroundColor: '#FFC004',
        color: '#000',
        fontWeight: 'bold',
        height: 36,
        minWidth: 50,
        '&:hover': {
            backgroundColor: '#e0a800',
        },
    },
    loopBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    loopBtnActive: {
        backgroundColor: '#4CAF50',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#388E3C',
        },
    },
}))

export default MeasureBandLocal
